import { Dispatch, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RootState } from 'store/rootReducer';
import { clearSearchAktivitetResult, clearSearchCriteria, searchAktivitet } from 'store/searchAktivitetSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaCheckbox, RaaSelect, RaaSpinner } from 'raa-react';

import {
  DrawerBody,
  DrawerContainer,
  DrawerSection,
  TabNavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import SearchFooter from 'components/search/SearchFooter';
import { LanKommunSelectWithTagList, buildQueryString, getLanAndKommunValues } from 'components/search/common';
import {
  SearchAktiviteterFormValues,
  searchInventeringSchema
} from 'components/search/inventering/searchInventeringSchema';
import TagList from 'components/tagList/TagList';

import { AktivitetStatusTyp, Utforare } from 'models';

import { AktivitetStatusEnum } from 'enums';

import classNames from 'classnames';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';

const SearchInventeringForm: FC = () => {
  const [isSearchingByInventeringsnummer, setIsSearchingByAktivitetsnummer] = useState<boolean>(false);
  const [aktivitetNotFound, setAktivitetNotFound] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const formikRef = useRef<FormikProps<SearchAktiviteterFormValues>>(null);

  const {
    requestStatus: aktivitetRequestStatus,
    data: searchedAktivitet,
    searchCriteria
  } = useAppSelector((state: RootState) => state.searchAktiviteter);

  const { requestStatus: aktivitetDomanRequestStatus, data: aktivitetDomaner } = useAppSelector(
    (state: RootState) => state.aktivitetDomaner
  );
  const { requestStatus: geoRequestStatus, data: geo } = useAppSelector((state: RootState) => state.geo);

  const { lanItems, kommunItems } = useMemo(() => getLanAndKommunValues(geo.kommun, geo.lan), [geo.kommun, geo.lan]);

  const [initialValues, setInitialValues] = useState<SearchAktiviteterFormValues>();

  const handleSubmit = async (values: SearchAktiviteterFormValues): Promise<void> => {
    if (values.aktivitetsnummer.length > 0) {
      dispatch(searchAktivitet({ criteria: { aktivitetsnummer: [values.aktivitetsnummer] } }));
    } else {
      const queryString = buildQueryString(values);
      navigate(`/sok-inventering/resultat?${queryString}&page=0`);
    }
  };

  const clearForm = () => {
    dispatch(clearSearchCriteria());
    setAktivitetNotFound(false);
    setIsSearchingByAktivitetsnummer(false);
    formikRef.current?.resetForm();
  };

  useEffect(() => {
    if (searchCriteria) {
      setInitialValues({
        aktivitetsnummer:
          searchCriteria.aktivitetsnummer && searchCriteria.aktivitetsnummer.length
            ? searchCriteria.aktivitetsnummer[0]
            : '',
        aktivitetstatustyper: searchCriteria.aktivitetstatustyper || [],
        ansvarig_organisation: searchCriteria.ansvarig_organisation || [],
        kommun_kod: searchCriteria.kommun_kod || [],
        lan_kod: searchCriteria.lan_kod || []
      });
    } else {
      setInitialValues({
        aktivitetsnummer: '',
        aktivitetstatustyper: [
          AktivitetStatusEnum.AVSLUTAD,
          AktivitetStatusEnum.PAGAENDE,
          AktivitetStatusEnum.PLANERAD
        ],
        ansvarig_organisation: [],
        kommun_kod: [],
        lan_kod: []
      });
    }
  }, [kommunItems, lanItems, searchCriteria]);

  useEffect(() => {
    if (formikRef.current?.values.aktivitetsnummer.length && searchedAktivitet?.results.length === 1) {
      dispatch(clearSearchAktivitetResult());
      navigate(`/aktivitet/${searchedAktivitet?.results[0].aktivitet_id}`);
    } else if (searchedAktivitet?.results.length === 0) {
      setAktivitetNotFound(true);
    }
  }, [searchedAktivitet, formikRef.current?.values.aktivitetsnummer, dispatch, navigate]);

  useEffect(() => {
    setAktivitetNotFound(false);
  }, [formikRef.current?.values.aktivitetsnummer]);

  return (
    <>
      {initialValues && (
        <DrawerContainer>
          <TabNavigationDrawerHead />
          <DrawerBody>
            <DrawerSection>
              <h1 className="tw-heading-md tw-mb-2">Sök inventering</h1>
              {aktivitetDomanRequestStatus === 'pending' || geoRequestStatus === 'pending' ? (
                <div className="tw-flex tw-justify-center tw-py-8">
                  <RaaSpinner size="3rem" />
                </div>
              ) : (
                <Formik
                  onSubmit={handleSubmit}
                  validationSchema={searchInventeringSchema}
                  enableReinitialize={true}
                  initialValues={initialValues}
                  innerRef={formikRef}
                >
                  {({ errors, touched }) => (
                    <Form id="searchForm" className="tw-h-full">
                      <section className="tw-border-b tw-border-raa-gray-3">
                        <Aktivitetsnummer hasValue={setIsSearchingByAktivitetsnummer} />
                        <p className="tw-h-6 tw-text-raa-error-red">
                          {formikRef.current &&
                            formikRef.current.values.aktivitetsnummer.length > 0 &&
                            errors.aktivitetsnummer &&
                            touched.aktivitetsnummer &&
                            errors.aktivitetsnummer}
                          {aktivitetNotFound && 'Ingen inventering hittades'}
                        </p>
                      </section>

                      <fieldset
                        disabled={isSearchingByInventeringsnummer}
                        className={classNames('tw-flex tw-flex-col', {
                          'tw-pointer-events-none tw-opacity-25': isSearchingByInventeringsnummer
                        })}
                      >
                        <legend className="tw-sr-only">Ange sökparametrar för sökning på inventeringar</legend>

                        <AnsvarigOrganisationWithTagList />

                        <LanKommunSelectWithTagList disabled={isSearchingByInventeringsnummer} />

                        <StatusSelectList statusTyper={aktivitetDomaner?.aktivitetstatustyper} />
                      </fieldset>
                    </Form>
                  )}
                </Formik>
              )}
            </DrawerSection>
          </DrawerBody>
          <SearchFooter
            domanRequestStatus={aktivitetDomanRequestStatus}
            searchRequestStatus={aktivitetRequestStatus}
            clearForm={clearForm}
          />
        </DrawerContainer>
      )}
    </>
  );
};

const Aktivitetsnummer: FC<{
  hasValue: Dispatch<SetStateAction<boolean>>;
}> = ({ hasValue }) => {
  const { setFieldValue, values } = useFormikContext<SearchAktiviteterFormValues>();
  const inputRef = useRef<HTMLInputElement>(null);

  const valueChanged = async (text: string) => {
    const formatted = formatAktivitetsnummer(text, values.aktivitetsnummer);
    let cursorPosition = inputRef.current && inputRef.current.selectionStart;
    if (
      ((cursorPosition === 1 && text !== 'C') || cursorPosition === 6) &&
      text.length > values.aktivitetsnummer.length
    ) {
      cursorPosition += 1;
    }
    await setFieldValue('aktivitetsnummer', formatted);
    inputRef.current && inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    hasValue(text !== '');
  };

  const formatAktivitetsnummer = (newValue: string, previousValue: string): string => {
    if (!newValue) return newValue;

    if (!previousValue || newValue.length > previousValue.length) {
      let result = '';

      for (let i = 0; i < newValue.length; i++) {
        const char = newValue[i];

        if (i == 0 && char === 'c') {
          result = 'C';
        } else if (i === 0 && char !== 'C') {
          result += 'C' + char;
        } else if (i === 5) {
          result += char === '-' ? char : '-' + char;
        } else if (char !== '-') {
          result += char;
        }
      }
      return result;
    }
    return newValue;
  };

  return (
    <div className="tw-mb-2 tw-w-1/2">
      <label className="raa-label form-label tw-flex tw-items-center" htmlFor="inventeringsnummer">
        Inventeringsnummer
      </label>
      <input
        id="inventeringsnummer"
        name="aktivitetsnummer"
        className="raa-input"
        type="text"
        placeholder="CÅÅÅÅ-N"
        value={values.aktivitetsnummer}
        ref={inputRef}
        onChange={(e) => valueChanged(e.target.value)}
      />
    </div>
  );
};

const StatusSelectList: FC<{
  statusTyper?: AktivitetStatusTyp[];
}> = ({ statusTyper }) => {
  const { setFieldValue, values } = useFormikContext<SearchAktiviteterFormValues>();

  const toggleStatus = async (status: AktivitetStatusTyp) => {
    if (values.aktivitetstatustyper.includes(status.aktivitetstatustyp)) {
      if (values.aktivitetstatustyper.length > 1) {
        // Minst en vara vara vald
        await setFieldValue(
          'aktivitetstatustyper',
          values.aktivitetstatustyper.filter((s) => status.aktivitetstatustyp !== s)
        );
      }
    } else {
      await setFieldValue('aktivitetstatustyper', [...values.aktivitetstatustyper, status.aktivitetstatustyp]);
    }
  };

  return (
    <fieldset className="tw-mt-4">
      <legend className="tw-font-semibold">Status</legend>
      <div data-testid="status-checkboxes" className="tw-flex tw-flex-grow tw-gap-4">
        {statusTyper &&
          statusTyper.map((status) => (
            <RaaCheckbox
              key={status.aktivitetstatustyp}
              checked={values.aktivitetstatustyper.includes(status.aktivitetstatustyp)}
              onChange={() => toggleStatus(status)}
              labelContent={status.namn}
            />
          ))}
      </div>
    </fieldset>
  );
};

const AnsvarigOrganisationWithTagList: FC = () => {
  const { setFieldValue, values } = useFormikContext<SearchAktiviteterFormValues>();
  const { requestStatus: utforareRequestStatus, data: utforare } = useAppSelector((state: RootState) => state.utforare);

  return (
    <>
      {utforare && utforareRequestStatus === 'idle' && (
        <>
          <label className="raa-label tw-mt-4" htmlFor="ansvarigOrganisation">
            Ansvarig organisation
          </label>
          <RaaSelect
            clearInputOnSelection={true}
            inputElementId="ansvarigOrganisation"
            domain={utforare.filter((utforare) => !values.ansvarig_organisation.includes(utforare.organisationsnummer))}
            displayAttr={'namn'}
            onSelect={async (uforare: Utforare) => {
              if (!values.ansvarig_organisation.includes(uforare.organisationsnummer)) {
                await setFieldValue('ansvarig_organisation', [
                  ...values.ansvarig_organisation.map((organisationsnummer) => organisationsnummer),
                  uforare.organisationsnummer
                ]);
              }
            }}
          />
          {
            <div className="tw-mt-2">
              <TagList
                list={utforare?.filter((utforare) =>
                  values.ansvarig_organisation.includes(utforare.organisationsnummer)
                )}
                leftColumn={(organisation: Utforare) => organisation.namn}
                onRemoveItem={async (toRemove: Utforare) => {
                  await setFieldValue(
                    'ansvarig_organisation',
                    values.ansvarig_organisation
                      .filter((organisationsnummer) => organisationsnummer !== toRemove.organisationsnummer)
                      .map((organisationsnummer) => organisationsnummer)
                  );
                }}
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default SearchInventeringForm;
